import { useEffect, useRef, useState } from "react";
import { UploadCloud, Eye, FileSearch, Trash2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";
import { api, errMsg, openBlob } from "../lib/api";
import { DOC_STATUS, DOC_TYPES, fdate, run, useLoad } from "../lib/util";
import { Badge, Btn, Card, SectionTitle, Table, Empty, Confirm } from "../components/kit";
import { useSchool } from "./ctx";
import DocReview from "./DocReview";
import Opening from "./Opening";

const RES = { enviado: ["Enviado — em leitura", "green"], conferir: ["Aguardando conferência", "amber"], duplicado: ["Duplicado — bloqueado", "red"], erro: ["Erro", "red"], enviando: ["Enviando…", "info"], aguardando: ["Na fila", "muted"] };

function Uploader({ type, onDone }) {
  const ref = useRef();
  const [batch, setBatch] = useState([]);
  const isNf = type.startsWith("nf_");
  const send = async (files) => {
    const list = Array.from(files).map((f) => ({ file: f, name: f.name, status: "aguardando" }));
    setBatch(list);
    for (let i = 0; i < list.length; i++) {
      list[i] = { ...list[i], status: "enviando" };
      setBatch([...list]);
      const fd = new FormData();
      fd.append("doc_type", type);
      fd.append("files", list[i].file);
      try {
        const { data } = await api.post("/s/documents/upload", fd);
        list[i] = { ...list[i], status: data[0].result, message: data[0].message };
      } catch (e) {
        list[i] = { ...list[i], status: "erro", message: errMsg(e) };
      }
      setBatch([...list]);
      onDone();
    }
  };
  const done = batch.filter((b) => !["aguardando", "enviando"].includes(b.status)).length;
  return (
    <Card>
      <div
        data-testid={`dropzone-${type}`}
        onClick={() => ref.current.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); send(e.dataTransfer.files); }}
        className="border-2 border-dashed border-slate-300 hover:border-[#FF6500] rounded-xl p-8 text-center cursor-pointer transition-colors bg-slate-50/50"
      >
        <UploadCloud className="w-9 h-9 mx-auto text-[#FF6500]" />
        <div className="font-semibold text-[#0B192C] mt-2">Arraste os arquivos ou clique para selecionar</div>
        <div className="text-xs text-slate-500 mt-1">PDF, JPG, PNG ou WEBP{isNf ? " · XML de NF-e" : ""} · envio em massa · até 20 MB por arquivo</div>
        <input ref={ref} data-testid={`upload-input-${type}`} type="file" multiple hidden accept={`.pdf,.jpg,.jpeg,.png,.webp${isNf ? ",.xml" : ""}`} onChange={(e) => { send(e.target.files); e.target.value = ""; }} />
      </div>
      {batch.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="batch-progress">
          <div className="flex justify-between text-xs text-slate-500"><span>Envio: {done} de {batch.length} arquivo(s)</span><span>{Math.round((done / batch.length) * 100)}%</span></div>
          <Progress value={(done / batch.length) * 100} className="h-2" />
          {batch.map((b, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`batch-item-${i}`}>
              <Badge tone={RES[b.status]?.[1]}>{RES[b.status]?.[0]}</Badge>
              <span className="font-medium">{b.name}</span>
              {b.message && <span className="text-xs text-slate-500">{b.message}</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function DocList({ type }) {
  const { tick, bump, month } = useSchool();
  const otherMonth = (d) => d.doc_type.startsWith("nf_") && !["confirmado", "descartado"].includes(d.status) && d.data?.issue_date && d.data.issue_date.slice(0, 7) !== month;
  const [docs, reload] = useLoad(async () => (await api.get("/s/documents", { params: type ? { doc_type: type } : {} })).data, [type, tick]);
  const [open, setOpen] = useState(null);
  const [del, setDel] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const pending = (docs || []).filter((d) => d.status === "extraido").length;
  const confirmAll = async () => {
    const off = (docs || []).filter((d) => d.status === "extraido" && otherMonth(d));
    if (off.length && !window.confirm(`${off.length} nota(s) com data de emissão fora do mês em trabalho (${month.slice(5)}/${month.slice(0, 4)}):\n${off.map((d) => `NF ${d.data.number || "?"} · ${fdate(d.data.issue_date)}`).join("\n")}\n\nElas entram no estoque do mês da emissão, não do mês em trabalho. Confirmar mesmo assim?`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/s/documents/confirm-batch", { doc_types: [type] })).data;
      setBatch(r);
      toast.success(`${r.confirmed.length} confirmado(s), ${r.skipped.length} com pendência`);
      reload();
      bump();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const remove = async () => {
    const ok = await run(() => api.delete(`/s/documents/${del.id}`), "Documento excluído");
    setDel(null);
    if (ok) {
      reload();
      bump();
    }
  };
  useEffect(() => {
    if (docs?.some((d) => d.status === "extraindo")) {
      const t = setTimeout(reload, 3000);
      return () => clearTimeout(t);
    }
  }, [docs, reload]);
  const label = (d) => {
    const x = d.data || {};
    if (d.doc_type.startsWith("nf_")) return `NF ${x.number || "?"} · ${x.supplier || "fornecedor pendente"} · ${fdate(x.issue_date) || "emissão pendente"}`;
    if (d.doc_type === "contrato_af") return `Contrato ${x.number || "?"} · ${x.supplier || ""}`;
    return x.reference_month ? `Referência ${x.reference_month.slice(5)}/${x.reference_month.slice(0, 4)}` : "";
  };
  return (
    <div className="space-y-3">
      {!type && <SectionTitle title="Documentos" subtitle="Todos os documentos enviados pela escola, com o original preservado para consulta." />}
      {type && pending > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Btn data-testid="doc-confirm-batch" disabled={busy} onClick={confirmAll}><CheckCheck className="w-4 h-4" />{busy ? "Confirmando…" : `Confirmar prontos (${pending} aguardando)`}</Btn>
          <span className="text-xs text-slate-500">Confirma de uma vez os documentos sem pendências (todos os produtos vinculados). Os demais continuam para conferência.</span>
        </div>
      )}
      {batch?.skipped.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs space-y-1" data-testid="doc-batch-skipped">
          <div className="font-semibold text-amber-800">Não confirmados — abra e confira:</div>
          {batch.skipped.map((s) => <div key={s.filename}><b>{s.filename}</b>: {s.reason}</div>)}
        </div>
      )}
      <Table testid={`doc-list-${type || "all"}`}>
        <thead><tr><th>Arquivo</th>{!type && <th>Tipo</th>}<th>Dados</th><th>Situação</th><th>Enviado</th><th /></tr></thead>
        <tbody>
          {(docs || []).map((d) => (
            <tr key={d.id} data-testid={`doc-row-${d.id}`}>
              <td className="font-medium max-w-[220px] truncate" title={d.filename}>{d.filename}</td>
              {!type && <td className="text-xs">{DOC_TYPES[d.doc_type].short}</td>}
              <td className="text-xs text-slate-600">{label(d)}{otherMonth(d) && <div className="text-amber-700 font-semibold" data-testid={`doc-other-month-${d.id}`}>Emissão em {d.data.issue_date.slice(5, 7)}/{d.data.issue_date.slice(0, 4)} — fora do mês em trabalho ({month.slice(5)}/{month.slice(0, 4)})</div>}{d.suspicion?.length > 0 && d.status !== "confirmado" && <div className="text-amber-700 font-semibold">Possível duplicidade — conferir</div>}</td>
              <td><Badge tone={DOC_STATUS[d.status][1]}>{DOC_STATUS[d.status][0]}</Badge></td>
              <td className="text-xs">{fdate(d.created_at)}</td>
              <td className="whitespace-nowrap">
                <Btn variant="ghost" title="Ver original" data-testid={`doc-view-${d.id}`} onClick={() => openBlob(`/s/documents/${d.id}/file`)}><Eye className="w-4 h-4" /></Btn>
                <Btn variant={d.status === "extraido" ? "primary" : "outline"} data-testid={`doc-review-${d.id}`} disabled={d.status === "extraindo"} onClick={() => setOpen(d.id)}><FileSearch className="w-4 h-4" />{d.status === "extraido" ? "Conferir" : "Abrir"}</Btn>
                <Btn variant="ghost" title="Excluir arquivo e dados" data-testid={`doc-delete-${d.id}`} onClick={() => setDel(d)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {docs && !docs.length && <Empty>Nenhum documento enviado.</Empty>}
      {open && <DocReview id={open} onClose={() => setOpen(null)} onChanged={() => { reload(); bump(); }} />}
      {del && (
        <Confirm title="Excluir documento" confirmLabel="Excluir" testid="doc-delete-dialog" onConfirm={remove} onClose={() => setDel(null)}>
          O arquivo "{del.filename}" e os dados lidos dele serão removidos{del.status === "confirmado" ? " — inclusive o que já entrou no estoque, no cardápio ou nos contratos" : ""}. Depois você pode enviar o arquivo corrigido novamente.
        </Confirm>
      )}
    </div>
  );
}

export default function DocSection({ type }) {
  const { bump } = useSchool();
  const t = DOC_TYPES[type];
  const hint = {
    mapa_anterior: "Necessário apenas na implantação: o saldo final do mapa anterior vira o estoque inicial. Nos meses seguintes, o saldo é transferido automaticamente.",
    cardapio: "A IA organiza as preparações por dia e refeição. Depois, vincule os gêneros de cada preparação em Formulários › Cardápio diário.",
    nf_principal: "As compras do fornecedor principal entram no estoque após a conferência e não descontam contratos da agricultura familiar.",
    contrato_af: "Itens com quantidade vazia ou zero não foram comprados e são ignorados. Após a confirmação, o contrato aparece em Agricultura familiar com o controle de execução.",
    nf_af: "Vincule cada nota ao contrato correto na conferência para atualizar o saldo a comprar.",
  }[type];
  return (
    <div className="space-y-5">
      <SectionTitle title={`${t.n}. ${t.label}`} subtitle={hint} />
      {type === "mapa_anterior" && <Opening />}
      <Uploader type={type} onDone={bump} />
      <DocList type={type} />
    </div>
  );
}
